import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/api";
import { Trash2 } from "lucide-react";

export default function Cart() {
  const { items, updateQty, removeItem, keyOf, subtotal, count } = useCart();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" data-testid="cart-page">
      <div className="max-w-[1500px] mx-auto px-3 md:px-6 py-6">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-9">
            <div className="card p-4 md:p-6">
              <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1a1d29] pb-3 border-b border-[#e0d8c8]">
                Your Bag
              </h1>

              {items.length === 0 ? (
                <div className="py-14 text-center">
                  <div className="font-display text-2xl font-semibold text-[#1a1d29]">Your bag is empty</div>
                  <div className="text-sm text-[#6b6b6b] mt-2">
                    Start with a few considered pieces.
                  </div>
                  <Link to="/shop" className="btn-primary inline-block mt-6" data-testid="cart-continue-shopping">
                    Continue shopping
                  </Link>
                </div>
              ) : (
                <>
                  <div className="text-sm text-right text-[#6b6b6b] mt-3">Price</div>
                  {items.map((it) => {
                    const k = keyOf(it);
                    return (
                      <div key={k} className="py-5 border-b border-[#e0d8c8] flex gap-4" data-testid={`cart-page-item-${it.product_id}`}>
                        <div className="w-32 h-40 bg-[#faf7f2] border border-[#e0d8c8] rounded-md overflow-hidden shrink-0">
                          {it.image && <img src={it.image} className="w-full h-full object-cover" alt={it.name} />}
                        </div>
                        <div className="flex-1 flex flex-col">
                          <div className="flex justify-between gap-3">
                            <div>
                              <Link to={`/product/${it.product_id}`} className="text-lg font-medium text-[#1a1d29] hover:text-[#8b3a3a] hover:underline">
                                {it.name}
                              </Link>
                              <div className="text-sm text-[#3a6a3a] mt-1">In Stock</div>
                              <div className="text-sm text-[#6b6b6b] mt-1">
                                {it.size ? `Size: ${it.size}` : ""}{it.size && it.color ? " · " : ""}{it.color ? `Colour: ${it.color}` : ""}
                              </div>
                              <div className="mt-4 flex items-center gap-3 text-sm">
                                <select
                                  value={it.quantity}
                                  onChange={(e) => updateQty(k, Number(e.target.value))}
                                  className="border border-[#e0d8c8] rounded-md h-9 px-3 bg-white"
                                  data-testid={`cart-qty-select-${it.product_id}`}
                                >
                                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                                    <option key={n} value={n}>Qty: {n}</option>
                                  ))}
                                </select>
                                <span className="text-[#e0d8c8]">|</span>
                                <button
                                  onClick={() => removeItem(k)}
                                  className="ec-link"
                                  data-testid={`cart-remove-${it.product_id}`}
                                >
                                  <Trash2 className="w-4 h-4 inline -mt-0.5 mr-1" /> Remove
                                </button>
                              </div>
                            </div>
                            <div className="font-display text-xl font-semibold text-[#1a1d29]">{formatINR(it.price * it.quantity)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="pt-5 text-right text-lg">
                    Subtotal ({count} item{count !== 1 ? "s" : ""}):{" "}
                    <span className="font-display font-semibold text-2xl" data-testid="cart-total">{formatINR(subtotal)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {items.length > 0 && (
            <div className="col-span-12 md:col-span-3">
              <div className="card p-5 sticky top-[130px]">
                <div className="text-sm">Subtotal ({count} item{count !== 1 ? "s" : ""})</div>
                <div className="font-display text-3xl font-semibold text-[#1a1d29] mt-1">{formatINR(subtotal)}</div>
                <button
                  onClick={() => navigate("/checkout")}
                  className="btn-primary mt-5 w-full text-sm"
                  data-testid="cart-page-checkout"
                >
                  Proceed to Checkout
                </button>
                <div className="mt-3 text-xs text-[#6b6b6b]">
                  Shipping and taxes calculated at checkout. Free shipping over ₹5,000.
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
